/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: opAgrume.js
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 24/9/2025 - 18:48
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */
import * as React from "react";
import { toast } from "react-toastify";
import { apiClient } from "../utils/utils.tsx";

/**
 * Display the OpAgrume tool
 *
 * @param {list<string>} domainsList the list of the available domains for the current user
 * @returns
 */
export default function OpAgrume({ domainsList }) {
   const errorMessageId = "errorMessage";
   const opDomainsSelectId = "opDomainsSelect";
   const agrDomainsSelectId = "agrDomainsSelect";
   const comparisonTableId = "comparisonTable";

   const [domainsSelect, setDomainsSelect] = React.useState({ opDom: [], agrDom: [] });
   const [comparisonTable, setComparisonTable] = React.useState([]);
   const [loading, setLoading] = React.useState([]);
   const [deleteDialog, setDeleteDialog] = React.useState("");
   const [errorMessage, setErrorMessage] = React.useState("");
   const [firstExec, setFirstExec] = React.useState(true);
   let comparisonTableArray = [];

   const checked = false;
   let autoSyncToggleChecked = false;
   let displayLoading = false;

   // Gestionnaire d'UI
   const UI = {
      elements: {
         errorMessage: document.getElementById(errorMessageId),
      },

      toggleLoading(show) {
         if (show != displayLoading) {
            if (show) {
               setLoading(
                  <div id="loading" className="flex justify-center align-middle items-center p-4">
                     <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
               );
            } else {
               setLoading(
                  <div id="loading" className="flex justify-center align-middle items-center p-4 hidden">
                     <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
               );
            }
            displayLoading = show;
         }
      },

      showError(message) {
         toast.error(message);
         setErrorMessage(
            <div id="errorMessage" className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
               <strong>{message}</strong>
            </div>
         );
      },

      hideError() {
         setErrorMessage("");
      },

      printTitledNumber(title, number) {
         return (
            <div className="titledNumber text-sm text-gray-600">
               {title}:{" "}
               <span className={title.toLowerCase() + " bg-blue-500 rounded px-1 text-white"}>
                  {number === -1 ? "..." : number}
               </span>
            </div>
         );
      },

      async populateDomainSelects() {
         const _opDomainsSelect = [];
         const _agrDomainsSelect = [];

         // Remplir le select avec les domaines openpaye
         const domains = await API.fetchDomains();

         const opDomains = [];
         for (let i = 0; i < domains.openpaye.length; i++) {
            if (domainsList.includes(domains.openpaye[i])) {
               opDomains.push(domains.openpaye[i]);
            }
         }
         const agrDomains = domains.agrume;

         if (opDomains) {
            // log list and current domain
            for (let i = 0; i < opDomains.length; i++) {
               if (opDomains[i] === (localStorage.getItem("opDomain") || opDomains[0])) {
                  localStorage.setItem("opDomain", opDomains[i]);
                  _opDomainsSelect.push(
                     <option key={i} value={opDomains[i]} selected={true}>
                        {opDomains[i]}
                     </option>
                  );
               } else {
                  _opDomainsSelect.push(
                     <option key={i} value={opDomains[i]} selected={false}>
                        {opDomains[i]}
                     </option>
                  );
               }
            }
         }

         if (agrDomains) {
            for (let i = 0; i < agrDomains.length; i++) {
               if (agrDomains[i] === (localStorage.getItem("agrDomain") || agrDomains[0])) {
                  localStorage.setItem("agrDomain", agrDomains[i]);
                  _agrDomainsSelect.push(
                     <option key={i} value={agrDomains[i]} selected={true}>
                        {agrDomains[i]}
                     </option>
                  );
               } else {
                  _agrDomainsSelect.push(
                     <option key={i} value={agrDomains[i]} selected={false}>
                        {agrDomains[i]}
                     </option>
                  );
               }
            }
         }
         setDomainsSelect({ opDom: _opDomainsSelect, agrDom: _agrDomainsSelect });
      },

      createTableRow(item, index) {
         const _openPayeCell = this.createOpenPayeCell(item);
         const _agrumeCell = this.createAgrumeCell(item);
         const _actionsCell = this.createActionsCell(item, index, "loading");
         const _removeCell = this.createRemoveCell(item);

         return (
            <tr key={index} className="scrollable-table-tr" data-item-index={index}>
               <td key="1" className="py-4 border-b">
                  <div className="align-middle text-center">
                     <span className="text-gray-300">{index}</span>
                  </div>
               </td>
               {_openPayeCell}
               {_agrumeCell}
               {_actionsCell}
               {_removeCell}
            </tr>
         );
      },

      updateTableRow(updatedItem, index) {
         if (comparisonTableArray.length <= index) {
            throw new Error(`Ligne ${index} introuvable`);
         }

         const _openPayeCell = this.createOpenPayeCell(updatedItem);
         const _agrumeCell = this.createAgrumeCell(updatedItem);
         const _actionsCell = this.createActionsCell(updatedItem, index, "loaded");
         const _removeCell = this.createRemoveCell(updatedItem);

         comparisonTableArray[index] = (
            <tr key={index + 1} className="scrollable-table-tr" data-item-index={index + 1}>
               <td key="1" className="py-4 border-b">
                  <div className="align-middle text-center">
                     <span className="text-gray-300">{index + 1}</span>
                  </div>
               </td>
               {_openPayeCell}
               {_agrumeCell}
               {_actionsCell}
               {_removeCell}
            </tr>
         );

         setComparisonTable(
            <table className="min-w-full shadow-md rounded scrollable-table">
               <thead className="scrollable-table-head">
                  <tr className="bg-gray-100">
                     <th key="1" className="py-3 border-b">
                        #
                     </th>
                     <th key="2" className="py-3 border-b">
                        OpenPaye
                     </th>
                     <th key="3" className="py-3 border-b">
                        Agrume
                     </th>
                     <th key="4" className="py-3 border-b" colSpan="2">
                        Actions
                     </th>
                     <th key="5" className="py-3 border-b" />
                  </tr>
               </thead>
               <tbody className="scrollable-table-body" id={comparisonTableId}>
                  {comparisonTableArray}
               </tbody>
            </table>
         );
      },

      createIndex(index) {
         const cell = document.createElement("td");
         cell.className = "py-4 border-b";

         cell.innerHTML = `
      <div class="align-middle text-center">
          <span class="text-gray-300">${index}</span>
      </div>
    `;

         return cell;
      },

      createOpenPayeCell(item) {
         return (
            <td key="2" className="px-6 py-4 border-b">
               <div className="font-semibold">
                  <strong>{item.opFolder.id}</strong> - {item.opFolder.name}
               </div>
               <div className="text-sm text-gray-600">SIRET: {item.opFolder.siret}</div>
               <div className="text-left px-full">
                  {this.printTitledNumber("Etablissements", item.opFolder.nbEtablishements)}
                  {this.printTitledNumber("Salariés", item.opFolder.nbEmployees)}
               </div>
            </td>
         );
      },

      createAgrumeCell(item) {
         if (item.employerAgrume) {
            return (
               <td key="3" className="px-6 py-4 border-b">
                  <div className="font-semibold">
                     <strong>{item.employerAgrume.id}</strong> - {item.employerAgrume.name}
                  </div>
                  <div className="text-left">
                     {this.printTitledNumber("Etablissements", item.employerAgrume.nbEtablishements)}
                     {this.printTitledNumber("Salariés", item.employerAgrume.nbEmployees)}
                  </div>
               </td>
            );
         } else {
            return (
               <td className="px-6 py-4 border-b">
                  <div className="text-gray-400 italic">Non synchronisé</div>
               </td>
            );
         }
      },

      createActionsCell(item, index, loadState) {
         const button = [];
         if (item.needsEmpSync) {
            button.push(
               <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  onClick={() => API.handleFolderSync(item.opFolder.id)}
               >
                  Synchroniser le dossier
               </button>
            );
         }

         if (item.needsEtabSync) {
            button.push(
               <button
                  className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600"
                  onClick={() => API.handleEtabSync(item.opFolder.id, item.employerAgrume.id)}
               >
                  Synchroniser les établissements
               </button>
            );
         }

         if (item.needsemployeesSync) {
            button.push(
               <button
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mt-2"
                  onClick={() => API.handleEmployeesSync(item.opFolder.id, item.employerAgrume.id)}
               >
                  Synchroniser les salariés
               </button>
            );
         }

         // Needs refresh datas:
         if (loadState !== "loaded") {
            button.push(
               <button
                  className="bg-sky-300 text-white px-4 py-2 rounded mt-2"
                  onClick={async () => {
                     const updatedItem = await API.updateFolderSync(item);
                     this.updateTableRow(updatedItem, index - 1);
                  }}
               >
                  Rafraîchir les données
               </button>
            );
         }

         return (
            <td key="4" className="px-6 py-4 border-b text-center">
               {button}
            </td>
         );
      },

      createRemoveCell(item) {
         let trash = "";
         if (item.employerAgrume) {
            trash = (
               <span className="cursor-pointer w-full flex">
                  <button className="transition-all text-red-400 hover:text-red-600">
                     <img src="images/trash.svg" onClick={() => this.showDeleteDialog(item.employerAgrume.id)} />
                  </button>
               </span>
            );
         }

         return (
            <td key="5" className="px-2 py-4 border-b text-center">
               {trash}
            </td>
         );
      },

      showDeleteDialog(employerId) {
         setDeleteDialog(
            <div className="fixed inset-0 delete-overlay flex items-center justify-center z-50">
               <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                  <div className="mb-4">
                     <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
                     <p className="text-gray-600 mb-4">Entrer le mot de passe pour supprimer l'employeur d'agrume.</p>
                     <input
                        type="password"
                        id="deletePassword"
                        placeholder="Enter password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                     />
                     <div id="passwordError" className="text-red-600 text-sm mt-2 hidden">
                        Mot de passe incorrect
                     </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                     <button
                        id="cancelDelete"
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                        onClick={this.closeDeleteDialog}
                     >
                        Annuler
                     </button>
                     <button
                        id="confirmDelete"
                        className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                        onClick={() => this.handlePasswordConfirm(employerId)}
                     >
                        Supprimer
                     </button>
                  </div>
               </div>
            </div>
         );
      },

      closeDeleteDialog() {
         setDeleteDialog();
      },

      async handlePasswordConfirm(employerId) {
         const passwordInput = document.getElementById("deletePassword");
         const errorDiv = document.getElementById("passwordError");
         const confirmBtn = document.getElementById("confirmDelete");

         // Disable button and show loading state
         confirmBtn.disabled = true;
         confirmBtn.textContent = "Verifying...";

         try {
            // Hash password with SHA256
            const hashedPassword = await this.sha256(passwordInput.value);

            // Send to server for validation
            API.postData("/api/opagrume/validate-password", { password: hashedPassword });

            // TODO: remove comment to actually delete agrume data
            API.handleDeleteAgrumeEmployer(employerId);
            this.closeDeleteDialog();
         } catch (error) {
            console.error("Password validation error:", error);

            // Add shake animation
            passwordInput.classList.add("animate-pulse");
            passwordInput.value = "";
            setTimeout(() => {
               passwordInput.classList.remove("animate-pulse");
            }, 500);

            errorDiv.textContent = "Server error. Please try again.";
            errorDiv.classList.remove("hidden");
         } finally {
            // Re-enable button
            confirmBtn.disabled = false;
            confirmBtn.textContent = "Delete";
         }
      },

      // SHA256 hash function using Web Crypto API
      async sha256(message) {
         // Encode the message as UTF-8
         const msgBuffer = new TextEncoder().encode(message);

         // Hash the message
         const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

         // Convert ArrayBuffer to hex string
         const hashArray = Array.from(new Uint8Array(hashBuffer));
         const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

         return hashHex;
      },

      clearComparisonTable() {
         // Vider le tableau avant de le remplir
         if (comparisonTable.length > 0) {
            comparisonTableArray = [];
            setComparisonTable(<table className="min-w-full shadow-md rounded scrollable-table hidden"></table>);
         }
      },

      updateTable(data) {
         comparisonTableArray = [];
         for (let i = 0; i < data.length; i++) {
            const itemMaybe = loadItemFromLocalStorage(data[i].opFolder.id);
            if (itemMaybe) {
               comparisonTableArray.push(this.createTableRow(itemMaybe, i + 1));
            } else {
               comparisonTableArray.push(this.createTableRow(data[i], i + 1));
            }
         }
         if (comparisonTable != comparisonTableArray) {
            setComparisonTable(
               <table className="min-w-full shadow-md rounded scrollable-table">
                  <thead className="scrollable-table-head">
                     <tr className="bg-gray-100">
                        <th key="1" className="py-3 border-b">
                           #
                        </th>
                        <th key="2" className="py-3 border-b">
                           OpenPaye
                        </th>
                        <th key="3" className="py-3 border-b">
                           Agrume
                        </th>
                        <th key="4" className="py-3 border-b" colSpan="2">
                           Actions
                        </th>
                        <th key="5" className="py-3 border-b" />
                     </tr>
                  </thead>
                  <tbody className="scrollable-table-body" id={comparisonTableId}>
                     {comparisonTableArray}
                  </tbody>
               </table>
            );
         }
      },
   };

   // Gestionnaire d'API
   const API = {
      async getData(url) {
         let returnData = "";
         try {
            const response = await apiClient.post("/redirOpagrum", { method: "GET", url: url });
            returnData = response.data;
         } catch (error) {
            toast.error(error);
         }
         return returnData;
      },

      async postData(url, data) {
         let returnData = "";
         try {
            const response = await apiClient.post("/redirOpagrum", { method: "POST", url: url, data: data });
            returnData = response.data;
         } catch (error) {
            toast.error(error);
         }
         return returnData;
      },

      async putData(url, data) {
         let returnData = "";
         try {
            const response = await apiClient.post("/redirOpagrum", { method: "PUT", url: url, data: data });
            returnData = response.data;
         } catch (error) {
            toast.error(error);
         }
         return returnData;
      },

      async deleteData(url, data) {
         let returnData = "";
         try {
            const response = await apiClient.post("/redirOpagrum", { method: "DELETE", url: url, data: data });
            returnData = response.data;
         } catch (error) {
            toast.error(error);
         }
         return returnData;
      },

      async fetchFoldersData() {
         const response = await this.postData("/api/opagrume/dossiers", cachedDomains());
         return response;
      },

      async updateFolderSync(item) {
         const { op, agr } = cachedDomains();

         const response = await this.putData("/api/opagrume/dossier", { op, agr, item });

         return response;
      },

      async fetchDomains() {
         const response = await this.getData("/api/opagrume/config/domains");
         return response;
      },

      async getAutoSyncStatus() {
         const response = await this.getData("/api/opagrume/sync/auto/status");
         return response.agrume;
      },

      async setAutoSyncStatus(enabled) {
         const { opDom, agrDom } = cachedDomains();

         const response = await this.postData("/api/opagrume/sync/auto/toggle", { opDom, agrDom, enabled });
         return response;
      },

      async syncEmployer(id) {
         const response = await this.postData(`/api/opagrume/sync/dossier/${id}`, cachedDomains());
         return response;
      },

      async deleteEmployers() {
         const response = await this.deleteData("/api/opagrume/sync/dossiers", cachedDomains());
         return response;
      },

      async syncEmployers() {
         const response = await this.postData("/api/opagrume/sync/dossiers", cachedDomains());
         return response;
      },

      async syncEstablishments(opId, agrId) {
         const response = await this.postData(`/api/opagrume/sync/${opId}/${agrId}/etablissements`, cachedDomains());
         return response;
      },

      async syncEmployees(opId, agrId) {
         const response = await this.postData(`/api/opagrume/sync/${opId}/${agrId}/salaries`, cachedDomains());
         return response;
      },

      async deleteEmployer(id) {
         const response = await this.deleteData(`/api/opagrume/sync/dossier/complete/${id}`, cachedDomains());
         return response;
      },

      async handleFolderSync(id) {
         try {
            UI.toggleLoading(true);
            UI.hideError();
            await this.syncEmployer(id);
         } catch (error) {
            UI.showError(`Erreur lors de la synchronisation: ${error.message}`);
         } finally {
            refreshData();
         }
      },

      async handleEtabSync(opId, agrId) {
         try {
            UI.toggleLoading(true);
            UI.hideError();
            if (opId === null || agrId === null) {
               UI.showError("id openpaye ou agrume vide");
            }
            await this.syncEstablishments(opId, agrId);
         } catch (error) {
            UI.showError(`Erreur lors de la synchronisation des Establishments: ${error.message}`);
         } finally {
            refreshData();
         }
      },

      async handleEmployeesSync(openPayeId, agrumeId) {
         try {
            UI.toggleLoading(true);
            UI.hideError();
            await this.syncEmployees(openPayeId, agrumeId);
         } catch (error) {
            UI.showError(`Erreur lors de la synchronisation des salariés: ${error.message}`);
         } finally {
            refreshData();
         }
      },

      async handleDeleteAgrumeEmployer(id) {
         try {
            UI.hideError();
            await this.deleteEmployer(id);
         } catch (error) {
            UI.showError(`Erreur lors de la suppression des Employers: ${error.message}`);
         } finally {
            refreshData();
         }
      },
   };

   // Fonction de rafraîchissement des données
   async function refreshData() {
      try {
         UI.hideError();
         UI.toggleLoading(true);
         if (comparisonTable.length > 0) {
            UI.clearComparisonTable();
         }
         const data = await API.fetchFoldersData();
         UI.updateTable(data);
         const searchData = getFilterTextContent();
         applyFilterOnComparisonTable(searchData);
      } catch (error) {
         UI.showError(`Erreur lors du chargement des données: ${error.message}`);
      } finally {
         UI.toggleLoading(false);
      }
   }

   function handleOpDomainChange(event) {
      const selectedDomain = event.target.value;
      if (selectedDomain) {
         UI.toggleLoading(true);
         UI.hideError();
         UI.clearComparisonTable();
         localStorage.setItem("opDomain", selectedDomain);
         refreshData();
      } else {
         UI.showError("Le domaine sélectionné est vide ou manquant");
      }
   }

   function handleAgrDomainChange(event) {
      const selectedDomain = event.target.value;
      if (selectedDomain) {
         UI.toggleLoading(true);
         UI.hideError();
         localStorage.setItem("agrDomain", selectedDomain);
         UI.clearComparisonTable();
         refreshData();
      } else {
         UI.showError("Le domaine sélectionné est vide ou manquant");
      }
   }

   function getFilterTextContent() {
      const textFilter = document.querySelector("#search");
      if (!textFilter) return "";

      return textFilter.value;
   }

   function applyFilterOnComparisonTable(searchValue) {
      // const searchValue = event.target.value;
      const rows = document.getElementById(comparisonTableId)?.children;

      if (!rows) {
         return;
      }

      if (searchValue.length < 3) {
         // If the search value is less than 3 characters, do not filter
         for (let i = 0; i < rows.length; i++) {
            const row = rows.item(i);
            if (row) {
               if (row.hasAttribute("style")) {
                  row.removeAttribute("style");
               }
            }
         }
         return;
      }

      // filter the items in real time in comparison-table based on the search value
      for (let i = 0; i < rows.length; i++) {
         const row = rows.item(i);
         if (row) {
            const cells = row.querySelectorAll("td");
            let found = false;
            cells.forEach((cell) => {
               if (cell.textContent.toLowerCase().includes(searchValue.toLowerCase())) {
                  found = true;
               }
            });
            if (found) {
               if (row.hasAttribute("style")) {
                  row.removeAttribute("style");
               }
            } else {
               row.setAttribute("style", "display: none;");
            }
         }
      }
   }

   // Fonction pour basculer la synchronisation automatique
   // deno-lint-ignore no-unused-vars
   function toggleAutoSync() {
      try {
         UI.toggleLoading(true);
         UI.hideError();
         API.setAutoSyncStatus(checked);
      } catch (error) {
         toast.error(`Erreur lors de la configuration de la synchronisation automatique: ${error.message}`);
         // Réinitialiser l'état du switch en cas d'erreur
         autoSyncToggleChecked = !checked;
      } finally {
         UI.toggleLoading(false);
      }
   }

   // Fonction pour charger l'état initial du switch
   function loadAutoSyncStatus() {
      try {
         const response = API.getAutoSyncStatus();
         autoSyncToggleChecked = response.enabled;
      } catch (error) {
         toast.error(`Erreur lors de la récupération de l'état de synchronisation: ${error.message}`);
      }
   }

   function cachedDomains() {
      const opDom = localStorage.getItem("opDomain");
      const agrDom = localStorage.getItem("agrDomain");
      if (!opDom || !agrDom) {
         const errMsg = "Sélectionner un domaine pour chaque plateforme";
         toast.error(errMsg);
         throw new Error(errMsg);
      }

      return { op: opDom, agr: agrDom };
   }

   function saveItemToLocalStorage(item) {
      const key = `dossier_${item.opFolder.id}`;
      localStorage.setItem(key, JSON.stringify(item));
   }

   // Au chargement de la page
   function loadItemFromLocalStorage(dossierId) {
      const key = `dossier_${dossierId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
   }

   if (firstExec) {
      setFirstExec(false);
      // Charger les données initiales
      UI.populateDomainSelects();
      refreshData();
      // Charger l'état de synchronisation automatique
      loadAutoSyncStatus();
   }

   return (
      <div className="container mx-auto">
         {deleteDialog}
         <h1 className="text-2xl font-bold mb-6">Synchronisation OpenPaye - Agrume</h1>
         {errorMessage}
         <div id="domain-selects" className="mb-4">
            <label htmlFor="op-domain-select" className="mr-2 font-medium">
               OpenPaye :
            </label>
            <select
               id={opDomainsSelectId}
               className="border border-gray-300 rounded px-4 py-2 mr-2 min-w-[200px]"
               onChange={handleOpDomainChange}
            >
               {domainsSelect.opDom}
            </select>
            <label htmlFor="agr-domain-select" className="mr-2 font-medium">
               Agrume :
            </label>
            <select
               id={agrDomainsSelectId}
               className="border border-gray-300 rounded px-4 py-2 mr-2 min-w-[200px]"
               onChange={handleAgrDomainChange}
            >
               {domainsSelect.agrDom}
            </select>
         </div>
         <div className="mb-4 flex items-center justify-between">
            <div>
               <button
                  onClick={refreshData}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mr-2"
               >
                  Rafraîchir les données
               </button>
               {/* <button onClick={syncAllAgrume()}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 mr-2 hidden">
                  Tous Synchroniser
               </button>
               <button onClick={syncAllIds()} className="hidden bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                  Synchroniser les identifiants
               </button> */}
            </div>
            <div className="hidden items-center">
               <span className="mr-3 font-medium">Synchronisation automatique</span>
               <label className="relative inline-flex items-center cursor-pointer hidden">
                  <input
                     type="checkbox"
                     ischecked={`${autoSyncToggleChecked}`}
                     id="auto-sync-toggle"
                     className="sr-only peer"
                     onChange={toggleAutoSync}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
               </label>
            </div>
         </div>
         <div className="overflow-x-auto justify-center items-center">
            <div className="items-center mb-4">
               <label htmlFor="search" className="mr-2 font-medium">
                  Rechercher :
               </label>{" "}
               <input
                  type="text"
                  id="search"
                  className="border border-gray-300 rounded px-4 py-2 flex-1 w-1/2"
                  placeholder="Entrer le nom d'un dossier, un id ou un siret..."
                  style={{ verticalAlign: "middle" }}
                  onInput={(event) => applyFilterOnComparisonTable(event.target.value)}
               />
            </div>
            {comparisonTable}
         </div>
         {loading}
      </div>
   );
}
